import type { SiteLayer } from '../../../types/site';

function canonicalNumber(value: number): number | string {
  return Number.isFinite(value) ? value : String(value);
}

function sortedNumberRecord(
  value: Record<string, number> | undefined,
): Array<[string, number | string]> | null {
  if (!value) return null;
  return Object.keys(value)
    .sort()
    .map((key) => [key, canonicalNumber(value[key])]);
}

/**
 * Immutable source identity consumed by the KMZ-to-canvas conversion.
 * Link fields and retrieval/write timestamps are deliberately excluded, so a
 * linkage-only write does not invalidate itself while any model-relevant
 * source drift fails closed.
 */
export function kmzConversionSourceFingerprint(layer: SiteLayer): string {
  const pipeline = layer.pipelineMeta;
  return JSON.stringify({
    id: layer.id,
    name: layer.name,
    category: layer.category,
    source: layer.source,
    geometry: {
      type: layer.geometry.type,
      coordinates: layer.geometry.coordinates.map((point) => [
        canonicalNumber(point.lat),
        canonicalNumber(point.lng),
      ]),
    },
    pipelineMeta: pipeline
      ? {
        source: pipeline.source,
        providerName: pipeline.providerName ?? null,
        providerRecordId: pipeline.providerRecordId ?? null,
        stalenessHours: pipeline.stalenessHours === undefined
          ? null
          : canonicalNumber(pipeline.stalenessHours),
        massFlow: pipeline.massFlow
          ? {
            value: canonicalNumber(pipeline.massFlow.value),
            unit: pipeline.massFlow.unit,
            basis: pipeline.massFlow.basis ?? null,
          }
          : null,
        gasComposition: sortedNumberRecord(pipeline.gasComposition),
        availabilityPct: pipeline.availabilityPct === undefined
          ? null
          : canonicalNumber(pipeline.availabilityPct),
        confidence: pipeline.confidence ?? null,
        notes: pipeline.notes ?? null,
      }
      : null,
  });
}
